import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, MenuItem, FormControl, InputLabel, Select, Chip, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Alert, Divider, TablePagination, TableFooter } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format } from 'date-fns';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import * as XLSX from 'xlsx';
import updatePlannedHours from './updatePlannedHours';
import { useAuth } from '../../context/Auth0Context';
import { useAppContext } from '../../context/AppContext';
import TimeIntervalInput from '../common/TimeIntervalInput';

const WeeklyPlan = () => {
  const { user: currentUser, isAdmin } = useAuth();
  const { refreshDashboardData } = useAppContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Helper function to format decimal hours as "Xh Ym"
  const formatHoursDisplay = (decimalHours) => {
    if (!decimalHours || decimalHours === 0) return '-';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);

    if (hours === 0) {
      return `${minutes}m`;
    } else if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  };
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [plannedHours, setPlannedHours] = useState([]);
  const [allPlannedHours, setAllPlannedHours] = useState([]);
  const [projectUsers, setProjectUsers] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // Reset current page if data shrinks past the last page (after filtering/deleting)
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(plannedHours.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [plannedHours.length, rowsPerPage, page]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const [jobNumbers, setJobNumbers] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [itemNumbers, setItemNumbers] = useState([]);
  const [newPlan, setNewPlan] = useState({
    project_id: '',
    user_id: '',
    hours: 0,
    hours_outside_normal: 0,
    job_number: '',
    project_lead: '',
    actual_hours_normal: 0,
    actual_hours_outside_normal: 0,
    comments: '',
    start_date: null,
    end_date: null,
    job_code_id: '',
    department_id: '',
    deliverable: ''
  });

  // State to store project leads (users with role 'project_manager')
  const [projectLeads, setProjectLeads] = useState([]);

  // State for project stages and job codes
  const [projectStages, setProjectStages] = useState([]);
  const [jobCodes, setJobCodes] = useState([]);
  const [departments, setDepartments] = useState([]);

  // State for revision history dialog
  const [openRevisionDialog, setOpenRevisionDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);

  // State for WPS completion status
  const [wpsStatus, setWpsStatus] = useState({});
  const [canCreateWPS, setCanCreateWPS] = useState(true);

  // State for allowable hours tracking
  const [allowableHoursStatus, setAllowableHoursStatus] = useState({});

  // State for success dialog
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    data: null
  });

  // State for job number filtering
  const [jobNumberInput, setJobNumberInput] = useState('');
  const [filteredJobNumbers, setFilteredJobNumbers] = useState([]);

  // State for inline editing of actual hours
  const [editingCellId, setEditingCellId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingHours, setEditingHours] = useState(0);
  const [editingMinutes, setEditingMinutes] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Handle URL parameters for notification navigation
  useEffect(() => {
    const filterUserParam = searchParams.get('filter_user');
    const highlightWpsParam = searchParams.get('highlight_wps');

    if (filterUserParam && isAdmin() && allUsers.length > 0) {
      // Find user by name for admin filtering
      const userToFilter = allUsers.find(user => 
        user.name.toLowerCase() === filterUserParam.toLowerCase()
      );
      
      if (userToFilter) {
        console.log(`🎯 Filtering to user: ${userToFilter.name} (ID: ${userToFilter.id})`);
        // Set the new plan user_id to pre-select this user in forms
        setNewPlan(prev => ({ ...prev, user_id: userToFilter.id }));
        
        // Filter planned hours to show only this user's data
        const userPlannedHours = allPlannedHours.filter(plan => plan.user_id === userToFilter.id);
        setPlannedHours(userPlannedHours);
        
        // If a specific WPS entry should be highlighted, scroll to it
        if (highlightWpsParam) {
          setTimeout(() => {
            const element = document.getElementById(`wps-${highlightWpsParam}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Add a temporary highlight effect
              element.style.backgroundColor = '#fff3e0';
              setTimeout(() => {
                element.style.backgroundColor = '';
              }, 3000);
            }
          }, 500);
        }
      }
    }
  }, [searchParams, allUsers, allPlannedHours, isAdmin]);



  // Filter job numbers as user types
  useEffect(() => {
    if (jobNumberInput.trim() === '') {
      setFilteredJobNumbers(jobNumbers);
    } else {
      const filtered = jobNumbers.filter(jobNum =>
        jobNum.toString().toLowerCase().includes(jobNumberInput.toLowerCase())
      );
      setFilteredJobNumbers(filtered);
    }
  }, [jobNumberInput, jobNumbers]);

  // Function to check WPS completion status for a user
  const checkWPSStatus = async (userId) => {
    if (!userId) return;

    try {
      const response = await axios.get(`/api/planned-hours/status?user_id=${userId}`, { withCredentials: true });
      setWpsStatus(prev => ({
        ...prev,
        [userId]: response.data
      }));

      // Update global canCreateWPS based on selected user
      if (newPlan.user_id === userId) {
        setCanCreateWPS(response.data.can_create_new_wps);
      }
    } catch (error) {
      console.error('Error checking WPS status:', error);
    }
  };

  // Function to check allowable hours status for a project
  const checkAllowableHoursStatus = async (projectId) => {
    if (!projectId) return;

    try {
      const response = await axios.get(`/api/projects/${projectId}/allowable-hours-status`, { withCredentials: true });
      setAllowableHoursStatus(prev => ({
        ...prev,
        [projectId]: response.data
      }));

      // The allowable hours status will be displayed in the UI automatically
    } catch (error) {
      console.error('Error checking allowable hours status:', error);
    }
  };

  useEffect(() => {
    const updateData = async () => {
      if (selectedProject) {
        await filterDataByProject(selectedProject);
      } else {
        // If no project is selected, show appropriate data based on user role
        if (isAdmin()) {
          // Admins can see all users
          setUsers(allUsers);
            } else {
      // Regular users only see themselves
      const currentUserInList = allUsers.find(user => 
        user.id === (currentUser?.employee_id || currentUser?.id)
      );
      setUsers(currentUserInList ? [currentUserInList] : []);
    }
        
        setPlannedHours(allPlannedHours);

        // Fetch all job numbers and item numbers from projects
        try {
          const [projectJobNumbersRes, projectItemNumbersRes] = await Promise.all([
            axios.get('/api/project-job-numbers', { withCredentials: true }),
            axios.get('/api/project-item-numbers', { withCredentials: true })
          ]);

          setJobNumbers(projectJobNumbersRes.data);
          setItemNumbers(projectItemNumbersRes.data);
        } catch (err) {
          console.error('Error fetching job and item numbers:', err);
        }
      }

      // Filter project leads (users with project_lead = 1)
      const leads = allUsers.filter(user => user.project_lead === 1);
      setProjectLeads(leads);
    };

    updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, allUsers, allPlannedHours, projectUsers, currentUser, isAdmin]);

  const fetchData = async () => {
    try {
      // Determine what planned hours to fetch based on user role
      const plannedHoursParams = {};
      if (!isAdmin() && currentUser) {
        // For regular users, fetch all their WPS entries
        // Use employee_id (database user ID) instead of Auth0 user ID
        plannedHoursParams.user_id = currentUser.employee_id || currentUser.id;
      }
      // For admins, fetch all WPS entries (no week filter needed)

      const [
        projectsRes,
        usersRes,
        plannedRes,
        projectUsersRes,
        projectJobNumbersRes,
        projectItemNumbersRes,
        projectStagesRes,
        jobCodesRes,
        departmentsRes
      ] = await Promise.all([
        axios.get('/api/projects', { withCredentials: true }),
        axios.get('/api/users', { withCredentials: true }),
        axios.get('/api/planned-hours', {
          params: plannedHoursParams,
          withCredentials: true
        }),
        axios.get('/api/project-users', { withCredentials: true }),
        axios.get('/api/project-job-numbers', { withCredentials: true }),
        axios.get('/api/project-item-numbers', { withCredentials: true }),
        axios.get('/api/project-stages', { withCredentials: true }),
        axios.get('/api/job-codes', { withCredentials: true }),
        axios.get('/api/departments', { withCredentials: true })
      ]);

      // Filter projects for regular users - only show projects they're assigned to
      let filteredProjects = projectsRes.data;
      if (!isAdmin() && currentUser) {
        const userProjectIds = projectUsersRes.data
          .filter(pu => pu.user_id === (currentUser.employee_id || currentUser.id))
          .map(pu => pu.project_id);
        filteredProjects = projectsRes.data.filter(p => userProjectIds.includes(p.id));
        console.log('🔒 Regular user - filtered to assigned projects:', filteredProjects.map(p => p.name));
      }

      setAllProjects(projectsRes.data);
      setProjects(filteredProjects);
      setFilteredProjects(filteredProjects);
      setAllUsers(usersRes.data);
      setUsers(usersRes.data);
      setAllPlannedHours(plannedRes.data);
      setPlannedHours(plannedRes.data);
      setProjectUsers(projectUsersRes.data);
      setJobNumbers(projectJobNumbersRes.data);
      setItemNumbers(projectItemNumbersRes.data);
      console.log('Fetched project stages:', projectStagesRes.data);
      console.log('Fetched job codes:', jobCodesRes.data);

      // Filter out any duplicates by ID
      const uniqueProjectStages = Array.from(
        new Map(projectStagesRes.data.map(stage => [stage.id, stage])).values()
      );
      const uniqueJobCodes = Array.from(
        new Map(jobCodesRes.data.map(code => [code.id, code])).values()
      );
      const uniqueDepartments = Array.from(
        new Map(departmentsRes.data.map(dept => [dept.id, dept])).values()
      );

      console.log('Unique project stages:', uniqueProjectStages);
      console.log('Unique job codes:', uniqueJobCodes);
      console.log('Unique departments:', uniqueDepartments);

      setProjectStages(uniqueProjectStages);
      setJobCodes(uniqueJobCodes);
      setDepartments(uniqueDepartments);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const filterDataByProject = async (projectId) => {
    // Filter users assigned to the selected project
    const assignedUserIds = projectUsers
      .filter(pu => pu.project_id === parseInt(projectId))
      .map(pu => pu.user_id);

    let filteredUsers = allUsers.filter(user => assignedUserIds.includes(user.id));

    // For regular users, further filter to only show themselves if they're assigned to the project
    if (!isAdmin() && currentUser) {
      const currentUserId = currentUser.employee_id || currentUser.id;
      filteredUsers = filteredUsers.filter(user => user.id === currentUserId);
    }

    // Filter planned hours for the selected project
    const filteredPlannedHours = allPlannedHours.filter(plan => plan.project_id === parseInt(projectId));

    // Get the selected project details
    const selectedProjectDetails = allProjects.find(p => p.id === parseInt(projectId));

    // Set job number from the project
    const projectJobNumber = selectedProjectDetails?.job_number || '';

    // Set job numbers array to include the project's value
    setJobNumbers([projectJobNumber].filter(Boolean));

    setUsers(filteredUsers);
    setPlannedHours(filteredPlannedHours);

    // Update the new plan with the selected project and its job number
    setNewPlan(prev => ({
      ...prev,
      project_id: projectId,
      user_id: '',
      job_number: projectJobNumber
    }));
  };

  const handleProjectFilterChange = async (event) => {
    const projectId = event.target.value;
    setSelectedProject(projectId);
    if (projectId) {
      await filterDataByProject(projectId);
    }
  };

  const clearProjectFilter = async () => {
    setSelectedProject('');
    setNewPlan(prev => ({
      ...prev,
      project_id: '',
      user_id: '',
      job_number: '',
      hours_outside_normal: 0,
      department_id: '',
      job_code_id: ''
    }));

    // Reset to appropriate data based on user role
    if (isAdmin()) {
      // Admins can see all users and all projects
      setUsers(allUsers);
      setFilteredProjects(allProjects);
            } else {
          // Regular users only see themselves
          const currentUserInList = allUsers.find(user => 
            user.id === (currentUser?.employee_id || currentUser?.id)
          );
          setUsers(currentUserInList ? [currentUserInList] : []);
          
          // Auto-select the current user if not already selected
          if (currentUserInList && !newPlan.user_id) {
            setNewPlan(prev => ({ ...prev, user_id: currentUserInList.id }));
          }
        }
    
    setPlannedHours(allPlannedHours);

    // Get all unique job numbers and item numbers from projects
    try {
      const [projectJobNumbersRes, projectItemNumbersRes] = await Promise.all([
        axios.get('/api/project-job-numbers', { withCredentials: true }),
        axios.get('/api/project-item-numbers', { withCredentials: true })
      ]);

      setJobNumbers(projectJobNumbersRes.data);
      setItemNumbers(projectItemNumbersRes.data);
    } catch (err) {
      console.error('Error fetching job and item numbers:', err);
    }
  };

  // Handle export to Excel (XLSX format)
  const handleExportToExcel = () => {
    try {
      // Define the column headers
      const headers = [
        'Item No.',
        'Job Number',
        'Project Name',
        'Start Date',
        'End Date',
        'Project Lead',
        'HOURS BETWEEN 8H00 and 17H00',
        'HOURS BEFORE 8H00 AND AFTER 17H00',
        'Actual Normal Hours',
        'Actual Hours Outside Normal',
        'Project Lead Hours',
        'Comments'
      ];

      // Format the data for export - create array of arrays for XLSX
      const exportData = plannedHours.map((plan, index) => {
        const project = allProjects.find((p) => p.id === plan.project_id);

        return [
          index + 1,                                                      // Item No.
          project?.job_number || '',                                        // Job Number
          project?.name || 'Unknown',                                       // Project Name
          plan.start_date ? format(new Date(plan.start_date), 'dd/MM/yyyy') : '',  // Start Date
          plan.end_date ? format(new Date(plan.end_date), 'dd/MM/yyyy') : '',      // End Date
          plan.project_lead || '',                                         // Project Lead
          parseFloat((plan.hours || 0).toFixed(2)),                        // Hours Between 8H00 and 17H00
          parseFloat((plan.hours_outside_normal || 0).toFixed(2)),         // Hours Before 8H00 and After 17H00
          parseFloat((plan.actual_hours_normal || 0).toFixed(2)),          // Actual Normal Hours
          parseFloat((plan.actual_hours_outside_normal || 0).toFixed(2)),  // Actual Hours Outside Normal
          parseFloat((plan.project_lead_hours_total || 0).toFixed(2)),     // Project Lead Hours
          plan.comments || ''                                              // Comments
        ];
      });

      // Create worksheet with headers and data
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...exportData]);

      // Set column widths for better readability
      worksheet['!cols'] = [
        { wch: 8 },   // Item No.
        { wch: 15 },  // Job Number
        { wch: 30 },  // Project Name
        { wch: 12 },  // Start Date
        { wch: 12 },  // End Date
        { wch: 20 },  // Project Lead
        { wch: 25 },  // Hours Between 8H00 and 17H00
        { wch: 28 },  // Hours Before 8H00 and After 17H00
        { wch: 18 },  // Actual Normal Hours
        { wch: 24 },  // Actual Hours Outside Normal
        { wch: 18 },  // Project Lead Hours
        { wch: 40 }   // Comments
      ];

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'WPS Export');

      // Generate filename with current date
      const dateStr = format(new Date(), 'yyyy-MM-dd');
      const fileName = `wps-export-${dateStr}.xlsx`;

      // Write and download the file
      XLSX.writeFile(workbook, fileName);

      console.log('Export successful');
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      alert('Failed to export data. Please try again.');
    }
  };

  // Filter projects when a user is selected (for admins)
  useEffect(() => {
    if (isAdmin()) {
      if (newPlan.user_id) {
        // Filter projects assigned to the selected user
        const assignedProjectIds = projectUsers
          .filter(pu => pu.user_id === parseInt(newPlan.user_id))
          .map(pu => pu.project_id);

        const userFilteredProjects = allProjects.filter(project =>
          assignedProjectIds.includes(project.id)
        );

        setFilteredProjects(userFilteredProjects);

        // If the currently selected project is not in the filtered list, reset it
        if (newPlan.project_id && !assignedProjectIds.includes(parseInt(newPlan.project_id))) {
          setNewPlan(prev => ({ ...prev, project_id: '' }));
        }

        // Filter planned hours to show only this user's data
        const userPlannedHours = allPlannedHours.filter(plan => plan.user_id === parseInt(newPlan.user_id));
        setPlannedHours(userPlannedHours);
      } else {
        // If no user is selected, show all projects
        setFilteredProjects(allProjects);
        setPlannedHours(allPlannedHours);
      }
    }
  }, [newPlan.user_id, allProjects, projectUsers, allPlannedHours, isAdmin]);

  const handlePlanSubmit = async (e) => {
    e.preventDefault();
    try {
      // Format dates for submission
      const formattedStartDate = newPlan.start_date ? format(newPlan.start_date, 'yyyy-MM-dd') : null;
      const formattedEndDate = newPlan.end_date ? format(newPlan.end_date, 'yyyy-MM-dd') : null;

      // Create the request payload with all required fields
      const payload = {
        ...newPlan,
        week_start: formattedStartDate || format(new Date(), 'yyyy-MM-dd'), // Use start_date or current date as week_start
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        // Remove explicit status - let it default to 'draft' in the database
        project_stage_id: newPlan.project_stage_id || null,
        job_code_id: newPlan.job_code_id || null,
        deliverable: newPlan.deliverable || ''
      };

      console.log('Submitting plan with payload:', payload);

      const response = await axios.post('/api/planned-hours', payload, { withCredentials: true });
      console.log('Plan submitted successfully:', response.data);

      // Refresh local data
      fetchData();
      
      // Refresh dashboard data in AppContext so it shows up immediately in dashboard
      try {
        await refreshDashboardData();
        console.log('✅ Dashboard data refreshed after WPS creation');
      } catch (error) {
        console.error('Failed to refresh dashboard data:', error);
        // Don't fail the whole operation if dashboard refresh fails
      }

      setNewPlan({
        project_id: '',
        user_id: '',
        hours: 0,
        hours_outside_normal: 0,
        job_number: '',
        project_lead: '',
        actual_hours_normal: 0,
        actual_hours_outside_normal: 0,
        comments: '',
        start_date: null,
        end_date: null,
        job_code_id: '',
        department_id: '',
        deliverable: '',
        project_stage_id: ''
      });
    } catch (err) {
      console.error('Error saving plan:', err);
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Server error response:', err.response.data);
        alert(`Error: ${err.response.data.error || 'Failed to save plan'}`);
      } else if (err.request) {
        // The request was made but no response was received
        console.error('No response received:', err.request);
        alert('Error: No response from server. Please try again.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Request setup error:', err.message);
        alert(`Error: ${err.message}`);
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleUpdateMissingData = async () => {
    try {
      await updatePlannedHours();
      // Refresh data after update
      fetchData();
      alert('Successfully updated planned hours with missing data!');
    } catch (error) {
      console.error('Error updating planned hours:', error);
      alert('Error updating planned hours. Please check the console for details.');
    }
  };



  // Function to open revision history dialog
  const handleOpenRevisionHistory = async (plan) => {
    setSelectedPlan(plan);
    try {
      const response = await axios.get(`/api/planned-hours/${plan.id}/revisions`, { withCredentials: true });
      setRevisions(response.data);
      setOpenRevisionDialog(true);
    } catch (error) {
      console.error('Error fetching revision history:', error);
      alert('Error fetching revision history. Please check the console for details.');
    }
  };

  // Function to close revision history dialog
  const handleCloseRevisionDialog = () => {
    setOpenRevisionDialog(false);
    setSelectedPlan(null);
    setRevisions([]);
  };

  // Function to handle editing a plan
  const handleEditPlan = (plan) => {
    if (plan.has_been_edited) {
      alert('This WPS entry has already been edited once and cannot be modified again.');
      return;
    }

    setEditingPlan({
      ...plan,
      start_date: plan.start_date ? new Date(plan.start_date) : null,
      end_date: plan.end_date ? new Date(plan.end_date) : null,
      // Initialize actual hours fields if they don't exist
      actual_hours_normal: plan.actual_hours_normal || 0,
      actual_hours_outside_normal: plan.actual_hours_outside_normal || 0
    });
    setOpenEditDialog(true);
  };

  // Function to close edit dialog
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingPlan(null);
  };

  // Function to save edited plan
  const handleSaveEditedPlan = async () => {
    try {
      // For regular users, only send the actual hours fields
      const payload = {
        actual_hours_normal: editingPlan.actual_hours_normal || 0,
        actual_hours_outside_normal: editingPlan.actual_hours_outside_normal || 0,
        reason: 'Updated actual hours'
      };

      const response = await axios.put(`/api/planned-hours/${editingPlan.id}`, payload, { withCredentials: true });

      // Show success dialog with detailed information
      setSuccessDialog({
        open: true,
        data: response.data
      });

      // Refresh local data
      fetchData();
      
      // Refresh dashboard data in AppContext so it shows up immediately in dashboard
      try {
        await refreshDashboardData();
        console.log('✅ Dashboard data refreshed after WPS edit');
      } catch (error) {
        console.error('Failed to refresh dashboard data:', error);
        // Don't fail the whole operation if dashboard refresh fails
      }

      handleCloseEditDialog();
    } catch (error) {
      console.error('Error updating plan:', error);
      if (error.response && error.response.status === 403) {
        alert('This WPS entry has already been edited once and cannot be modified again.');
      } else {
        alert('Error updating actual hours. Please check the console for details.');
      }
    }
  };

  const handleNavigateToTimesheet = (plan) => {
    // Navigate to timesheet with filters for the specific project and user
    const params = new URLSearchParams({
      project_id: plan.project_id,
      user_id: plan.user_id,
      from_wps: 'true'
    });
    navigate(`/time-logging?${params.toString()}`);
  };

  // Handle inline editing of actual hours
  const handleStartEditingCell = (planId, fieldName, currentValue) => {
    setEditingCellId(`${planId}-${fieldName}`);
    setEditingValue(currentValue || '');

    // Convert decimal hours to hours and minutes
    const decimalValue = parseFloat(currentValue) || 0;
    const hours = Math.floor(decimalValue);
    const minutes = Math.round((decimalValue - hours) * 60);
    setEditingHours(hours);
    setEditingMinutes(minutes);
  };

  const handleSaveInlineEdit = async (planId, fieldName) => {
    // Prevent double-clicks
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      // Convert hours and minutes to decimal
      const numValue = editingHours + (editingMinutes / 60);

      // Find the current plan to get the other actual hours field
      const currentPlan = plannedHours.find(p => p.id === planId);

      // Build payload with BOTH actual hours fields to ensure correct total calculation
      const payload = {
        actual_hours_normal: fieldName === 'actual_hours_normal' ? numValue : (currentPlan?.actual_hours_normal || 0),
        actual_hours_outside_normal: fieldName === 'actual_hours_outside_normal' ? numValue : (currentPlan?.actual_hours_outside_normal || 0),
        reason: `Updated ${fieldName}`
      };

      await axios.put(`/api/planned-hours/${planId}`, payload, { withCredentials: true });

      // Refresh data
      await fetchData();

      // Refresh dashboard data
      try {
        await refreshDashboardData();
      } catch (error) {
        console.error('Failed to refresh dashboard data:', error);
      }

      setEditingCellId(null);
      setEditingValue('');
      setEditingHours(0);
      setEditingMinutes(0);
    } catch (error) {
      console.error('Error updating actual hours:', error);
      alert('Error updating actual hours. Please try again.');
      setEditingCellId(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelInlineEdit = () => {
    setEditingCellId(null);
    setEditingValue('');
    setEditingHours(0);
    setEditingMinutes(0);
  };

  const handleSaveProjectLeadHours = async (planId) => {
    // Prevent double-clicks
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      // Convert hours and minutes to decimal
      const numValue = editingHours + (editingMinutes / 60);

      // Call backend to create time entry for project lead
      await axios.post(`/api/planned-hours/${planId}/project-lead-hours`, {
        hours: numValue
      }, { withCredentials: true });

      // Refresh data
      await fetchData();

      // Refresh dashboard data
      try {
        await refreshDashboardData();
      } catch (error) {
        console.error('Failed to refresh dashboard data:', error);
      }

      setEditingCellId(null);
      setEditingValue('');
      setEditingHours(0);
      setEditingMinutes(0);
    } catch (error) {
      console.error('Error saving project lead hours:', error);
      alert(error.response?.data?.error || 'Error saving project lead hours. Please try again.');
      setEditingCellId(null);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom>
          Work Planning
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel id="project-filter-label">Filter by Project</InputLabel>
            <Select
              labelId="project-filter-label"
              value={selectedProject}
              onChange={handleProjectFilterChange}
              label="Filter by Project"
              startAdornment={<FilterListIcon sx={{ mr: 1, color: 'action.active' }} />}
            >
              <MenuItem value="">All Projects</MenuItem>
              {filteredProjects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedProject && (
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearProjectFilter}
              size="small"
            >
              Clear Filter
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportToExcel}
            disabled={plannedHours.length === 0}
            size="small"
            sx={{
              bgcolor: '#2D3A45',
              '&:hover': { bgcolor: '#3D4A55' }
            }}
          >
            Export to Excel
          </Button>
        </Box>
      </Box>

      <Box component="form" onSubmit={handlePlanSubmit} sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Add New Plan
        </Typography>
        {/* First row: Project Name, Job Number */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl sx={{ flex: 1 }} fullWidth>
            <InputLabel id="project-select-label">Project Name</InputLabel>
            <Select
              labelId="project-select-label"
              value={newPlan.project_id}
              onChange={async (e) => {
                const projectId = e.target.value;

                if (projectId) {
                  // Get the selected project details
                  const selectedProjectDetails = allProjects.find(p => p.id === parseInt(projectId));
                  const projectJobNumber = selectedProjectDetails?.job_number || '';

                  setNewPlan({
                    ...newPlan,
                    project_id: projectId,
                    user_id: '',
                    job_number: projectJobNumber
                  });

                  // Check allowable hours status for the selected project
                  await checkAllowableHoursStatus(projectId);

                  // If a project is selected in the form, also update the filter
                  if (projectId !== selectedProject) {
                    setSelectedProject(projectId);
                    await filterDataByProject(projectId);
                  }
                } else {
                  setNewPlan({
                    ...newPlan,
                    project_id: '',
                    user_id: '',
                    job_number: ''
                  });

                  await clearProjectFilter();
                }
              }}
              label="Project Name"
            >
              <MenuItem value="">Select a project</MenuItem>
              {filteredProjects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flex: 1, position: 'relative' }}>
            <TextField
              label="Job Number"
              value={jobNumberInput || newPlan.job_number}
              onChange={async (e) => {
                const value = e.target.value;
                setJobNumberInput(value);

                // If the field is cleared, reset the project selection
                if (value === '' && newPlan.project_id) {
                  setNewPlan({
                    ...newPlan,
                    project_id: '',
                    user_id: '',
                    job_number: ''
                  });
                  await clearProjectFilter();
                }
              }}
              placeholder="Type to search job numbers"
              fullWidth
              size="medium"
            />
            {jobNumberInput && filteredJobNumbers.length > 0 && (
              <Paper sx={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: 200, overflow: 'auto', zIndex: 1000, mt: 1 }}>
                {filteredJobNumbers.map((jobNum) => (
                  <Box
                    key={jobNum}
                    onClick={async () => {
                      // Find the project with this job number
                      const projectWithJobNumber = allProjects.find(p => p.job_number === jobNum);
                      if (projectWithJobNumber) {
                        setNewPlan(prev => ({
                          ...prev,
                          job_number: jobNum,
                          project_id: projectWithJobNumber.id
                        }));
                        // Clear the input to hide the dropdown
                        setJobNumberInput('');

                        // Check allowable hours status for the selected project
                        await checkAllowableHoursStatus(projectWithJobNumber.id);

                        // Update the filter if different from current
                        if (projectWithJobNumber.id !== selectedProject) {
                          setSelectedProject(projectWithJobNumber.id);
                          await filterDataByProject(projectWithJobNumber.id);
                        }
                      }
                    }}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      borderBottom: '1px solid #eee'
                    }}
                  >
                    {jobNum}
                  </Box>
                ))}
              </Paper>
            )}
          </Box>
        </Box>

        {/* Second row: Project Stage, Department */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl sx={{ flex: 1 }} fullWidth>
            <InputLabel id="project-stage-select-label">Project Stage</InputLabel>
            <Select
              labelId="project-stage-select-label"
              value={newPlan.project_stage_id || ''}
              onChange={(e) => setNewPlan({ ...newPlan, project_stage_id: e.target.value })}
              label="Project Stage"
              disabled={!newPlan.project_id}
            >
              <MenuItem value=""><em>Select a project stage</em></MenuItem>
              {projectStages.map((stage) => (
                <MenuItem key={`stage-${stage.id}`} value={stage.id}>
                  {stage.stage_number > 6 ? stage.name : `Stage ${stage.stage_number}: ${stage.name}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ flex: 1 }} fullWidth>
            <InputLabel id="department-select-label">Department</InputLabel>
            <Select
              labelId="department-select-label"
              value={newPlan.department_id || ''}
              onChange={(e) => setNewPlan({ ...newPlan, department_id: e.target.value })}
              label="Department"
              disabled={!newPlan.project_id}
            >
              <MenuItem value=""><em>Select a department</em></MenuItem>
              {departments.map((dept) => (
                <MenuItem key={`dept-${dept.id}`} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Deliverable on its own full-width row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="Deliverable"
            value={newPlan.deliverable || ''}
            onChange={(e) => setNewPlan({ ...newPlan, deliverable: e.target.value })}
            placeholder="Enter deliverable"
            variant="outlined"
          />
        </Box>

        {/* Third row: Project Lead, User */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>

          <FormControl sx={{ flex: 1 }} fullWidth>
            <InputLabel id="project-lead-select-label">Project Lead</InputLabel>
            <Select
              labelId="project-lead-select-label"
              value={newPlan.project_lead}
              onChange={(e) => setNewPlan({ ...newPlan, project_lead: e.target.value })}
              label="Project Lead"
            >
              <MenuItem value=""><em>Select a project lead</em></MenuItem>
              {projectLeads.map((lead) => (
                <MenuItem key={lead.id} value={lead.name}>
                  {lead.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ flex: 1 }} fullWidth>
            <InputLabel id="user-select-label">User</InputLabel>
            <Select
              labelId="user-select-label"
              value={newPlan.user_id}
              onChange={async (e) => {
                const userId = e.target.value;
                setNewPlan({ ...newPlan, user_id: userId });
                if (userId) {
                  await checkWPSStatus(userId);
                } else {
                  setCanCreateWPS(true);
                }
              }}
              label="User"
              disabled={false}
            >
              <MenuItem value="">Select a user</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                  {wpsStatus[user.id] && !wpsStatus[user.id].can_create_new_wps && (
                    <span style={{ color: 'red', marginLeft: '8px' }}>
                      ({wpsStatus[user.id].incomplete_wps_count} incomplete WPS)
                    </span>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Fourth row: Activity Start/End Dates */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Activity Start Date"
                value={newPlan.start_date}
                format="dd/MM/yyyy"
                onChange={(newValue) => {
                  setNewPlan({ ...newPlan, start_date: newValue });
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "medium"
                  }
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={{ flex: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Activity End Date"
                value={newPlan.end_date}
                format="dd/MM/yyyy"
                onChange={(newValue) => {
                  setNewPlan({ ...newPlan, end_date: newValue });
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "medium"
                  }
                }}
              />
            </LocalizationProvider>
          </Box>

        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {/* First column - Hours Between 8h00 and 17h00 */}
          <Box sx={{ flex: 1 }}>
            <TimeIntervalInput
              label="HOURS BETWEEN 8H00 and 17H00"
              value={newPlan.hours || 0}
              onChange={async (value) => {
                setNewPlan({ ...newPlan, hours: value });

                // Check allowable hours when hours change
                if (newPlan.project_id && value) {
                  await checkAllowableHoursStatus(newPlan.project_id);
                }
              }}
            />
          </Box>

          {/* Second column - Hours Before 8h00 and After 17h00 */}
          <Box sx={{ flex: 1 }}>
            <TimeIntervalInput
              label="HOURS BEFORE 8H00 AND AFTER 17H00"
              value={newPlan.hours_outside_normal || 0}
              onChange={(value) => {
                setNewPlan({ ...newPlan, hours_outside_normal: value });
              }}
            />
          </Box>
        </Box>

        {/* Comments in a separate row */}
        <Box sx={{ mb: 2 }}>
          <TextField
            label="Comments"
            value={newPlan.comments}
            onChange={(e) => setNewPlan({ ...newPlan, comments: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
        </Box>

        {/* WPS Restriction Warning */}
        {newPlan.user_id && !canCreateWPS && (
          <Box 
            sx={{ 
              mb: 2, 
              p: 2, 
              bgcolor: '#fff3e0', 
              borderRadius: 1,
              border: 2,
              borderColor: 'warning.main',
              boxShadow: 1
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#000000', 
                fontWeight: 500,
                '& strong': {
                  color: 'warning.main'
                }
              }}
            >
              ⚠️ This user has <strong>{wpsStatus[newPlan.user_id]?.incomplete_wps_count || 0}</strong> incomplete WPS entries.
              Please complete existing WPS entries before creating new ones.
            </Typography>
          </Box>
        )}

        {/* Allowable Hours Warning */}
        {newPlan.project_id && allowableHoursStatus[newPlan.project_id] && allowableHoursStatus[newPlan.project_id].has_allowable_hours_set && (
          <Box 
            sx={{ 
              mb: 2, 
              p: 2, 
              bgcolor: allowableHoursStatus[newPlan.project_id].is_over_limit ? '#ffebee' : '#e3f2fd', 
              borderRadius: 1,
              border: 2,
              borderColor: allowableHoursStatus[newPlan.project_id].is_over_limit ? 'error.main' : 'info.main',
              boxShadow: 1
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#000000', 
                fontWeight: 500,
                '& strong': {
                  color: allowableHoursStatus[newPlan.project_id].is_over_limit ? 'error.main' : 'info.main'
                }
              }}
            >
              📊 <strong>Project Allowable Hours:</strong> {allowableHoursStatus[newPlan.project_id].allowable_hours}h | 
              <strong>Used:</strong> {allowableHoursStatus[newPlan.project_id].total_planned_hours}h ({allowableHoursStatus[newPlan.project_id].utilization_percentage}%) | 
              <strong>Remaining:</strong> {allowableHoursStatus[newPlan.project_id].remaining_hours}h
              {allowableHoursStatus[newPlan.project_id].is_over_limit && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'error.light', borderRadius: 0.5, border: '1px solid', borderColor: 'error.main' }}>
                  <Typography variant="body2" sx={{ color: '#000000', fontWeight: 'bold' }}>
                    ⚠️ This project has exceeded its allowable hours limit!
                  </Typography>
                </Box>
              )}
            </Typography>
          </Box>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={!newPlan.project_id || !newPlan.user_id || !newPlan.hours || (!canCreateWPS && newPlan.user_id)}
        >
          Add Plan
        </Button>
      </Box>

      {plannedHours.length > 0 ? (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: 55, maxWidth: 55 }}>Item No.</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 90, maxWidth: 90 }}>Job Number</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>Project Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Start Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>End Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Project Lead</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>HOURS BETWEEN 8H00 and 17H00</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>HOURS BEFORE 8H00 AND AFTER 17H00</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actual Normal Hours</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actual Hours Outside Normal</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Project Lead Hours</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 250, maxWidth: 250 }}>Comments</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plannedHours
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((plan, index) => {
                const project = allProjects.find((p) => p.id === plan.project_id);
                const itemNo = page * rowsPerPage + index + 1;
                // We don't need the user variable since we're using plan.project_lead directly

                return (
                  <TableRow key={plan.id} hover>
                    <TableCell sx={{ width: 55, maxWidth: 55 }}>
                      <Typography variant="body2">
                        {itemNo}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: 90, maxWidth: 90 }}>
                      <Typography variant="body2">
                        {project?.job_number || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 400 }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 1.4 }}>
                        {project?.name || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {plan.start_date ? format(new Date(plan.start_date), 'dd/MM/yyyy') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {plan.end_date ? format(new Date(plan.end_date), 'dd/MM/yyyy') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {plan.project_lead || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {formatHoursDisplay(plan.hours)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {formatHoursDisplay(plan.hours_outside_normal)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}>
                      {editingCellId === `${plan.id}-actual_hours_normal` ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                          <TextField
                            type="number"
                            size="small"
                            label="Hours"
                            value={editingHours === 0 ? '' : editingHours}
                            onChange={(e) => setEditingHours(e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ width: 80 }}
                            autoFocus
                          />
                          <FormControl size="small" sx={{ width: 80 }}>
                            <InputLabel>Min</InputLabel>
                            <Select
                              value={editingMinutes}
                              onChange={(e) => setEditingMinutes(parseInt(e.target.value))}
                              label="Min"
                            >
                              <MenuItem value={0}>0</MenuItem>
                              <MenuItem value={5}>5</MenuItem>
                              <MenuItem value={10}>10</MenuItem>
                              <MenuItem value={15}>15</MenuItem>
                              <MenuItem value={20}>20</MenuItem>
                              <MenuItem value={25}>25</MenuItem>
                              <MenuItem value={30}>30</MenuItem>
                              <MenuItem value={35}>35</MenuItem>
                              <MenuItem value={40}>40</MenuItem>
                              <MenuItem value={45}>45</MenuItem>
                              <MenuItem value={50}>50</MenuItem>
                              <MenuItem value={55}>55</MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleSaveInlineEdit(plan.id, 'actual_hours_normal')}
                            disabled={isSaving}
                            sx={{ minWidth: 'auto', p: 0.5 }}
                          >
                            {isSaving ? '...' : '✓'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={handleCancelInlineEdit}
                            disabled={isSaving}
                            sx={{ minWidth: 'auto', p: 0.5 }}
                          >
                            ✕
                          </Button>
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          onClick={() => handleStartEditingCell(plan.id, 'actual_hours_normal', plan.actual_hours_normal)}
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline', fontWeight: 'bold' } }}
                        >
                          {formatHoursDisplay(plan.actual_hours_normal)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}>
                      {editingCellId === `${plan.id}-actual_hours_outside_normal` ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                          <TextField
                            type="number"
                            size="small"
                            label="Hours"
                            value={editingHours === 0 ? '' : editingHours}
                            onChange={(e) => setEditingHours(e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ width: 80 }}
                            autoFocus
                          />
                          <FormControl size="small" sx={{ width: 80 }}>
                            <InputLabel>Min</InputLabel>
                            <Select
                              value={editingMinutes}
                              onChange={(e) => setEditingMinutes(parseInt(e.target.value))}
                              label="Min"
                            >
                              <MenuItem value={0}>0</MenuItem>
                              <MenuItem value={5}>5</MenuItem>
                              <MenuItem value={10}>10</MenuItem>
                              <MenuItem value={15}>15</MenuItem>
                              <MenuItem value={20}>20</MenuItem>
                              <MenuItem value={25}>25</MenuItem>
                              <MenuItem value={30}>30</MenuItem>
                              <MenuItem value={35}>35</MenuItem>
                              <MenuItem value={40}>40</MenuItem>
                              <MenuItem value={45}>45</MenuItem>
                              <MenuItem value={50}>50</MenuItem>
                              <MenuItem value={55}>55</MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleSaveInlineEdit(plan.id, 'actual_hours_outside_normal')}
                            disabled={isSaving}
                            sx={{ minWidth: 'auto', p: 0.5 }}
                          >
                            {isSaving ? '...' : '✓'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={handleCancelInlineEdit}
                            disabled={isSaving}
                            sx={{ minWidth: 'auto', p: 0.5 }}
                          >
                            ✕
                          </Button>
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          onClick={() => handleStartEditingCell(plan.id, 'actual_hours_outside_normal', plan.actual_hours_outside_normal)}
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline', fontWeight: 'bold' } }}
                        >
                          {formatHoursDisplay(plan.actual_hours_outside_normal)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}>
                      {editingCellId === `${plan.id}-project_lead_hours` ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                          <TextField
                            type="number"
                            size="small"
                            label="Hours"
                            value={editingHours === 0 ? '' : editingHours}
                            onChange={(e) => setEditingHours(e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ width: 80 }}
                            autoFocus
                          />
                          <FormControl size="small" sx={{ width: 80 }}>
                            <InputLabel>Min</InputLabel>
                            <Select
                              value={editingMinutes}
                              onChange={(e) => setEditingMinutes(parseInt(e.target.value))}
                              label="Min"
                            >
                              <MenuItem value={0}>0</MenuItem>
                              <MenuItem value={5}>5</MenuItem>
                              <MenuItem value={10}>10</MenuItem>
                              <MenuItem value={15}>15</MenuItem>
                              <MenuItem value={20}>20</MenuItem>
                              <MenuItem value={25}>25</MenuItem>
                              <MenuItem value={30}>30</MenuItem>
                              <MenuItem value={35}>35</MenuItem>
                              <MenuItem value={40}>40</MenuItem>
                              <MenuItem value={45}>45</MenuItem>
                              <MenuItem value={50}>50</MenuItem>
                              <MenuItem value={55}>55</MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleSaveProjectLeadHours(plan.id)}
                            disabled={isSaving}
                            sx={{ minWidth: 'auto', p: 0.5 }}
                          >
                            {isSaving ? '...' : '✓'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={handleCancelInlineEdit}
                            sx={{ minWidth: 'auto', p: 0.5 }}
                          >
                            ✕
                          </Button>
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          onClick={() => handleStartEditingCell(plan.id, 'project_lead_hours', plan.project_lead_hours_total || 0)}
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline', fontWeight: 'bold' } }}
                        >
                          {formatHoursDisplay(plan.project_lead_hours_total)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 250, maxWidth: 250 }}>
                      <Typography variant="body2" sx={{
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word',
                        lineHeight: 1.4
                      }}>
                        {plan.comments || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {/* Edit button hidden as per user request */}
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            disabled={plan.has_been_edited}
                            onClick={() => handleEditPlan(plan)}
                            sx={{ display: 'none' }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="info"
                            onClick={() => handleOpenRevisionHistory(plan)}
                          >
                            History
                          </Button>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                          {/* Editable/Locked status hidden as per user request */}
                          {plan.has_been_edited ? (
                            <Chip
                              label="Edited (Locked)"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ display: 'none' }}
                            />
                          ) : (
                            <Chip
                              label="Editable"
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ display: 'none' }}
                            />
                          )}
                          {plan.timesheet_filled && (
                            <Chip
                              label="Timesheet Created"
                              size="small"
                              color="primary"
                              variant="outlined"
                              onClick={() => handleNavigateToTimesheet(plan)}
                              sx={{
                                display: 'none', // Hidden as per user request, but functionality retained
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  transform: 'scale(1.05)',
                                },
                                transition: 'all 0.2s ease-in-out'
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TablePagination
                count={plannedHours.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[15, 30, 50]}
                labelRowsPerPage="Rows per page:"
              />
            </TableFooter>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            No WPS entries found
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {selectedProject
              ? `There are no WPS entries for the selected project yet.`
              : isAdmin()
              ? `There are no WPS entries for the selected week.`
              : `You haven't created any WPS entries yet.`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use the form above to create your first WPS entry.
          </Typography>
        </Box>
      )}

      {/* Revision History Dialog */}
      <Dialog
        open={openRevisionDialog}
        onClose={handleCloseRevisionDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Revision History
        </DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
              Project: {allProjects.find(p => p.id === selectedPlan.project_id)?.name || 'Unknown'}
            </Typography>
          )}
          {revisions.length === 0 ? (
            <DialogContentText>No revision history found.</DialogContentText>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Revision</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Changes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {revisions.map((revision) => {
                    // Handle JSON data safely - could be string (SQLite) or object (Supabase)
                    let previousData = null;
                    let newData = null;

                    try {
                      if (revision.previous_data) {
                        // If it's already an object (Supabase), use it directly
                        if (typeof revision.previous_data === 'object') {
                          previousData = revision.previous_data;
                        } else {
                          // If it's a string (SQLite), parse it
                          previousData = JSON.parse(revision.previous_data);
                        }
                      }
                    } catch (error) {
                      console.error('Error parsing previous_data:', error, revision.previous_data);
                      previousData = null;
                    }

                    try {
                      if (revision.new_data) {
                        // If it's already an object (Supabase), use it directly
                        if (typeof revision.new_data === 'object') {
                          newData = revision.new_data;
                        } else {
                          // If it's a string (SQLite), parse it
                          newData = JSON.parse(revision.new_data);
                        }
                      }
                    } catch (error) {
                      console.error('Error parsing new_data:', error, revision.new_data);
                      newData = null;
                    }

                    // Determine what changed
                    let changes = [];
                    try {
                      if (previousData && newData && typeof previousData === 'object' && typeof newData === 'object') {
                        Object.keys(newData).forEach(key => {
                          if (previousData[key] !== newData[key]) {
                            changes.push(`${key}: ${previousData[key] || 'none'} → ${newData[key] || 'none'}`);
                          }
                        });
                      } else if (newData && typeof newData === 'object') {
                        changes.push('Initial creation');
                      } else {
                        changes.push('No changes detected');
                      }
                    } catch (error) {
                      console.error('Error calculating changes:', error);
                      changes.push('Error calculating changes');
                    }

                    if (changes.length === 0) {
                      changes.push('No changes detected');
                    }

                    return (
                      <TableRow key={revision.id}>
                        <TableCell>{revision.revision_number}</TableCell>
                        <TableCell>{new Date(revision.revision_date).toLocaleString()}</TableCell>
                        <TableCell>{revision.user_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={revision.revision_type}
                            color={
                              revision.revision_type === 'create' ? 'success' :
                              revision.revision_type === 'update' ? 'primary' :
                              revision.revision_type === 'status_change' ? 'warning' : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{revision.reason || '-'}</TableCell>
                        <TableCell>
                          <ul style={{ margin: 0, paddingLeft: 16 }}>
                            {changes.map((change, index) => (
                              <li key={index}>{change}</li>
                            ))}
                          </ul>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRevisionDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit WPS Entry</DialogTitle>
        <DialogContent>
          {editingPlan && (
            <Box sx={{ pt: 2 }}>
              {/* Project Info (Read-only) */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label="Project"
                  value={allProjects.find(p => p.id === editingPlan.project_id)?.name || ''}
                  fullWidth
                  disabled
                />
              </Box>

              {/* Show info message about editable fields */}
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: '#e3f2fd',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'info.main',
                  boxShadow: 1
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#000000',
                    fontWeight: 500,
                    '& strong': {
                      color: 'info.main'
                    }
                  }}
                >
                  ℹ️ Only the <strong>"Actual Normal Hours"</strong> and <strong>"Actual Hours Outside Normal"</strong> fields can be edited. Other fields are read-only.
                </Typography>
              </Box>

              {/* Hours fields - Read-only for regular users */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label="Hours Between 8H00 and 17H00"
                  type="number"
                  value={editingPlan.hours === 0 ? '' : editingPlan.hours || ''}
                  fullWidth
                  disabled
                />
                <TextField
                  label="Hours Before 8H00 and After 17H00"
                  type="number"
                  value={editingPlan.hours_outside_normal === 0 ? '' : editingPlan.hours_outside_normal || ''}
                  fullWidth
                  disabled
                />
              </Box>

              {/* Actual Hours - Always editable */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label="Actual Normal Hours"
                  type="number"
                  value={editingPlan.actual_hours_normal === 0 ? '' : editingPlan.actual_hours_normal || ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                    setEditingPlan({ ...editingPlan, actual_hours_normal: value });
                  }}
                  fullWidth
                  inputProps={{ min: 0, step: 0.5 }}
                />
                <TextField
                  label="Actual Hours Outside Normal"
                  type="number"
                  value={editingPlan.actual_hours_outside_normal === 0 ? '' : editingPlan.actual_hours_outside_normal || ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                    setEditingPlan({ ...editingPlan, actual_hours_outside_normal: value });
                  }}
                  fullWidth
                  inputProps={{ min: 0, step: 0.5 }}
                />
              </Box>

              {/* Date fields - Read-only for regular users */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Activity Start Date"
                    value={editingPlan.start_date}
                    format="dd/MM/yyyy"
                    slotProps={{ textField: { fullWidth: true } }}
                    disabled
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Activity End Date"
                    value={editingPlan.end_date}
                    format="dd/MM/yyyy"
                    slotProps={{ textField: { fullWidth: true } }}
                    disabled
                  />
                </LocalizationProvider>
              </Box>

              {/* Comments field - Read-only for regular users */}
              <TextField
                label="Comments"
                value={editingPlan.comments || ''}
                fullWidth
                multiline
                rows={3}
                sx={{ mb: 2 }}
                disabled
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleSaveEditedPlan} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog for WPS Updates */}
      <Dialog 
        open={successDialog.open} 
        onClose={() => setSuccessDialog({ open: false, data: null })}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
          <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
            ✅ WPS Entry Updated Successfully
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {successDialog.data && (
            <Box>
              {/* Timesheet Update Info */}
              {successDialog.data.timesheet_message && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Timesheet Update:</strong><br />
                    {successDialog.data.timesheet_message}
                  </Typography>
                </Alert>
              )}

              {/* Simplified Hours Summary */}
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Hours Summary
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      Hours Between 8H00-17H00:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {successDialog.data.original_hours_between || 0}h
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      Hours Before 8H00/After 17H00:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {successDialog.data.original_hours_before || 0}h
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      Actual Hours Between 8H00-17H00:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary.main">
                      {successDialog.data.actual_hours_normal || 0}h
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      Actual Hours Before 8H00/After 17H00:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary.main">
                      {successDialog.data.actual_hours_outside_normal || 0}h
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.50', p: 1, borderRadius: 1 }}>
                    <Typography variant="body1" color="text.secondary" fontWeight="bold">
                      Total Actual Hours Worked:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary.main">
                      {((successDialog.data.actual_hours_normal || 0) + (successDialog.data.actual_hours_outside_normal || 0))}h
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      Variance (Planned - Actual):
                    </Typography>
                    <Typography 
                      variant="body1" 
                      fontWeight="bold" 
                      color={
                        (successDialog.data.variance || 0) > 0 ? 'success.main' : 
                        (successDialog.data.variance || 0) < 0 ? 'error.main' : 'text.primary'
                      }
                    >
                      {(successDialog.data.variance || 0) > 0 ? '+' : ''}{successDialog.data.variance || 0}h
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSuccessDialog({ open: false, data: null })}
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WeeklyPlan;